export const bookExportTargets = ["pdf", "docx", "both"] as const;
export type BookExportTarget = (typeof bookExportTargets)[number];
export type RenderedBookExportTarget = Exclude<BookExportTarget, "both">;

export const bookExportModes = [
  "story_only",
  "side_stories_only",
  "full_compendium",
] as const;
export type BookExportMode = (typeof bookExportModes)[number];

export const bookExportPresets = [
  "classic_white",
  "book_cream",
  "parchment",
  "slate_dark",
  "minimal_modern",
] as const;
export type BookExportPreset = (typeof bookExportPresets)[number];

export const bookExportPageSizes = [
  "a5",
  "five_by_eight",
  "six_by_nine",
  "letter",
  "a4",
] as const;
export type BookExportPageSize = (typeof bookExportPageSizes)[number];

export const bookExportMessageSources = [
  "none",
  "author_note",
  "dedication",
  "special_thanks",
] as const;
export type BookExportFrontMatterMessageSource = Extract<
  (typeof bookExportMessageSources)[number],
  "none" | "author_note" | "dedication"
>;
export type BookExportEndMatterMessageSource = Extract<
  (typeof bookExportMessageSources)[number],
  "none" | "author_note" | "special_thanks"
>;

export const bookExportReportEntryKinds = [
  "warning",
  "omission",
  "fallback",
] as const;
export type BookExportReportEntryKind = (typeof bookExportReportEntryKinds)[number];

export const bookExportJobStatuses = [
  "pending",
  "processing",
  "complete",
  "failed",
] as const;
export type BookExportJobStatus = (typeof bookExportJobStatuses)[number];

export const bookExportCurrentStages = [
  "validating",
  "building_manifest",
  "rendering_pdf",
  "rendering_docx",
  "uploading_artifacts",
  "finalizing",
] as const;
export type BookExportCurrentStage = (typeof bookExportCurrentStages)[number];

export const bookExportSectionKeys = [
  "cover",
  "title_page",
  "table_of_contents",
  "main_story",
  "side_stories",
  "characters_appendix",
  "lore_appendix",
  "timeline_appendix",
  "end_matter",
] as const;
export type BookExportSectionKey = (typeof bookExportSectionKeys)[number];

export const bookExportTiers = ["free", "crafter", "author"] as const;
export type BookExportTier = (typeof bookExportTiers)[number];

export interface BookExportConfig {
  target: BookExportTarget;
  exportMode: BookExportMode;
  preset: BookExportPreset;
  pageSize: BookExportPageSize;
  printMode: boolean;
  mirroredMargins: boolean;
  startChaptersOnRightPage: boolean;
  showPageNumbers: boolean;
  includeCover: boolean;
  includeTitlePage: boolean;
  includeToc: boolean;
  includeIllustrations: boolean;
  selectedSideStoryIds: string[];
  includeCharacterAppendix: boolean;
  includeLoreAppendix: boolean;
  includeTimelineAppendix: boolean;
  frontMatterMessageSource: BookExportFrontMatterMessageSource;
  endMatterMessageSource: BookExportEndMatterMessageSource;
}

export interface BookExportProjectOverrides {
  authorName?: string | null;
  bookSubtitle?: string | null;
  bookTitle?: string | null;
  coverAssetPath?: string | null;
  seriesName?: string | null;
  volumeLabel?: string | null;
}

export interface BookExportProjectIdentity {
  id: string;
  title: string;
  subtitle: string | null;
  author_name: string;
  series_name: string | null;
  volume_label: string | null;
  cover_image_path: string | null;
}

export interface BookExportBookMetadata {
  author_note_html: string | null;
  dedication_html: string | null;
  special_thanks_html: string | null;
}

export type BookExportIllustrationRole =
  | "chapter_opener"
  | "inline"
  | "full_page_plate";
export type BookExportIllustrationAlignment = "center" | "left" | "right";
export type BookExportIllustrationSizeMode = "inline" | "wide" | "full_page";

export interface BookExportIllustration {
  asset_id: string;
  role: BookExportIllustrationRole;
  caption: string | null;
  alignment: BookExportIllustrationAlignment;
  size_mode: BookExportIllustrationSizeMode;
}

export interface BookExportMainStoryChapter {
  id: string;
  title: string;
  order_index: number;
  content_html: string;
  illustrations: BookExportIllustration[];
}

export interface BookExportSideStoryManifestEntry {
  id: string;
  title: string;
  order_index: number;
  content_html: string;
}

export interface BookExportCharacterAppendixEntry {
  id: string;
  title: string;
  body_html: string;
  image_asset_id: string | null;
}

export interface BookExportLoreAppendixEntry {
  id: string;
  title: string;
  type: string;
  body_html: string;
  tags: string[];
}

export interface BookExportTimelineAppendixEntry {
  id: string;
  era_label: string | null;
  title: string;
  body: string;
}

export interface CanonicalBookExportManifest {
  project: {
    id: string;
    title: string;
    subtitle: string | null;
    author_name: string;
    series_name: string | null;
    volume_label: string | null;
    cover_image_path: string | null;
  };
  export_profile: {
    target: BookExportTarget;
    export_mode: BookExportMode;
    preset: BookExportPreset;
    page_size: BookExportPageSize;
    print_mode: boolean;
    mirrored_margins: boolean;
    start_chapters_on_right_page: boolean;
    show_page_numbers: boolean;
  };
  front_matter: {
    include_cover: boolean;
    include_title_page: boolean;
    author_note_html: string | null;
    dedication_html: string | null;
    include_toc: boolean;
  };
  main_story: {
    include: boolean;
    chapters: BookExportMainStoryChapter[];
  };
  side_stories: {
    include: boolean;
    stories: BookExportSideStoryManifestEntry[];
  };
  appendices: {
    characters: {
      include: boolean;
      entries: BookExportCharacterAppendixEntry[];
    };
    lore: {
      include: boolean;
      entries: BookExportLoreAppendixEntry[];
    };
    timeline: {
      include: boolean;
      entries: BookExportTimelineAppendixEntry[];
    };
  };
  end_matter: {
    special_thanks_html: string | null;
  };
}

export interface BookExportManifestSource {
  project: BookExportProjectIdentity;
  bookMetadata: BookExportBookMetadata;
  mainStoryChapters: BookExportMainStoryChapter[];
  sideStories: BookExportSideStoryManifestEntry[];
  characterAppendixEntries: BookExportCharacterAppendixEntry[];
  loreAppendixEntries: BookExportLoreAppendixEntry[];
  timelineAppendixEntries: BookExportTimelineAppendixEntry[];
}

export interface BookExportArtifact {
  target: RenderedBookExportTarget;
  fileName: string;
  storagePath: string;
  mimeType: string;
  byteSize: number;
  createdAt: string;
}

export type BookExportReportSourceKind =
  | "project"
  | "chapter"
  | "side_story"
  | "entity_card"
  | "lore_entry"
  | "timeline_event"
  | "asset";

export interface BookExportReportEntry {
  kind: BookExportReportEntryKind;
  code: string;
  sectionKey: BookExportSectionKey | string;
  sourceKind: BookExportReportSourceKind;
  sourceId: string | null;
  message: string;
}

export interface BookExportReport {
  generatedAt: string;
  generatedSections: BookExportSectionKey[];
  entries: BookExportReportEntry[];
}

export interface BookExportJob {
  id: string;
  projectId: string;
  status: BookExportJobStatus;
  currentStage: BookExportCurrentStage | null;
  retryCount: number;
  completedAt: string | null;
  failureCode: string | null;
  failureMessage: string | null;
  leaseExpiresAt: string | null;
  artifacts: BookExportArtifact[];
  report: BookExportReport | null;
  config: BookExportConfig;
  manifestSnapshot: CanonicalBookExportManifest | null;
  updatedAt: string;
}

export interface BookExportSummarySection {
  key: BookExportSectionKey;
  label: string;
  included: boolean;
  itemCount: number;
}

export interface BookExportSummary {
  target: BookExportTarget;
  exportMode: BookExportMode;
  preset: BookExportPreset;
  pageSize: BookExportPageSize;
  sections: BookExportSummarySection[];
  warnings: string[];
}

export interface BookExportCapabilityMatrix {
  tier: BookExportTier;
  recommendedUpgradeTier: BookExportTier | null;
  allowedTargets: BookExportTarget[];
  allowedModes: BookExportMode[];
  allowedPresets: BookExportPreset[];
  allowedPageSizes: BookExportPageSize[];
  targetRequirements: Record<BookExportTarget, BookExportTier>;
  modeRequirements: Record<BookExportMode, BookExportTier>;
  presetRequirements: Record<BookExportPreset, BookExportTier>;
  frontMatterSourceRequirements: Record<BookExportFrontMatterMessageSource, BookExportTier>;
  endMatterSourceRequirements: Record<BookExportEndMatterMessageSource, BookExportTier>;
  allowIllustrations: boolean;
  illustrationsRequiredTier: BookExportTier;
  allowPrintMode: boolean;
  printModeRequiredTier: BookExportTier;
  allowMirroredMargins: boolean;
  mirroredMarginsRequiredTier: BookExportTier;
  allowRightPageStarts: boolean;
  rightPageStartsRequiredTier: BookExportTier;
  allowAppendices: boolean;
  appendixRequiredTier: BookExportTier;
  defaultConfig: BookExportConfig;
}

export const bookExportTargetRequirements = {
  pdf: "free",
  docx: "free",
  both: "author",
} as const satisfies Record<BookExportTarget, BookExportTier>;

export const bookExportModeRequirements = {
  story_only: "free",
  side_stories_only: "crafter",
  full_compendium: "author",
} as const satisfies Record<BookExportMode, BookExportTier>;

export const bookExportPresetRequirements = {
  classic_white: "free",
  book_cream: "crafter",
  parchment: "author",
  slate_dark: "author",
  minimal_modern: "crafter",
} as const satisfies Record<BookExportPreset, BookExportTier>;

export const bookExportFrontMatterSourceRequirements = {
  none: "free",
  author_note: "crafter",
  dedication: "author",
} as const satisfies Record<BookExportFrontMatterMessageSource, BookExportTier>;

export const bookExportEndMatterSourceRequirements = {
  none: "free",
  author_note: "crafter",
  special_thanks: "crafter",
} as const satisfies Record<BookExportEndMatterMessageSource, BookExportTier>;

export const bookExportIllustrationsRequiredTier: BookExportTier = "crafter";
export const bookExportPrintModeRequiredTier: BookExportTier = "author";
export const bookExportMirroredMarginsRequiredTier: BookExportTier = "author";
export const bookExportRightPageStartsRequiredTier: BookExportTier = "author";
export const bookExportAppendixRequiredTier: BookExportTier = "author";

function dedupeStrings(values: string[]) {
  return [...new Set(values.filter((value) => value.trim().length > 0))];
}

export function isBookExportTarget(value: unknown): value is BookExportTarget {
  return typeof value === "string" && (bookExportTargets as readonly string[]).includes(value);
}

export function isBookExportMode(value: unknown): value is BookExportMode {
  return typeof value === "string" && (bookExportModes as readonly string[]).includes(value);
}

export function isBookExportPreset(value: unknown): value is BookExportPreset {
  return typeof value === "string" && (bookExportPresets as readonly string[]).includes(value);
}

export function isBookExportPageSize(value: unknown): value is BookExportPageSize {
  return typeof value === "string" && (bookExportPageSizes as readonly string[]).includes(value);
}

export function isBookExportCurrentStage(value: unknown): value is BookExportCurrentStage {
  return typeof value === "string"
    && (bookExportCurrentStages as readonly string[]).includes(value);
}

export function isBookExportFrontMatterMessageSource(
  value: unknown,
): value is BookExportFrontMatterMessageSource {
  return value === "none" || value === "author_note" || value === "dedication";
}

export function isBookExportEndMatterMessageSource(
  value: unknown,
): value is BookExportEndMatterMessageSource {
  return value === "none" || value === "author_note" || value === "special_thanks";
}

export function createDefaultBookExportConfig(
  overrides: Partial<BookExportConfig> = {},
): BookExportConfig {
  return {
    target: "pdf",
    exportMode: "story_only",
    preset: "classic_white",
    pageSize: "a5",
    printMode: false,
    mirroredMargins: false,
    startChaptersOnRightPage: false,
    showPageNumbers: true,
    includeCover: true,
    includeTitlePage: true,
    includeToc: true,
    includeIllustrations: false,
    selectedSideStoryIds: dedupeStrings(overrides.selectedSideStoryIds ?? []),
    includeCharacterAppendix: false,
    includeLoreAppendix: false,
    includeTimelineAppendix: false,
    frontMatterMessageSource: "author_note",
    endMatterMessageSource: "special_thanks",
    ...overrides,
  };
}

export function getBookExportTierRank(tier: BookExportTier) {
  switch (tier) {
    case "author":
      return 2;
    case "crafter":
      return 1;
    default:
      return 0;
  }
}

export function isBookExportTierAllowed(
  tier: BookExportTier,
  requiredTier: BookExportTier,
) {
  return getBookExportTierRank(tier) >= getBookExportTierRank(requiredTier);
}

function collectAllowedValues<T extends string>(
  requirements: Record<T, BookExportTier>,
  tier: BookExportTier,
) {
  return (Object.entries(requirements) as Array<[T, BookExportTier]>)
    .filter(([, requiredTier]) => isBookExportTierAllowed(tier, requiredTier))
    .map(([value]) => value);
}

function getRecommendedBookExportUpgradeTier(
  tier: BookExportTier,
): BookExportTier | null {
  switch (tier) {
    case "free":
      return "crafter";
    case "crafter":
      return "author";
    default:
      return null;
  }
}

function createDefaultBookExportConfigForTier(
  tier: BookExportTier,
) {
  switch (tier) {
    case "free":
      return createDefaultBookExportConfig({
        endMatterMessageSource: "none",
        exportMode: "story_only",
        frontMatterMessageSource: "none",
        includeIllustrations: false,
        preset: "classic_white",
        target: "pdf",
      });
    case "crafter":
      return createDefaultBookExportConfig({
        endMatterMessageSource: "special_thanks",
        exportMode: "story_only",
        frontMatterMessageSource: "author_note",
        includeIllustrations: true,
        preset: "book_cream",
        target: "pdf",
      });
    default:
      return createDefaultBookExportConfig({
        endMatterMessageSource: "special_thanks",
        exportMode: "full_compendium",
        frontMatterMessageSource: "author_note",
        includeCharacterAppendix: true,
        includeIllustrations: true,
        includeLoreAppendix: true,
        includeTimelineAppendix: true,
        preset: "parchment",
        target: "both",
      });
  }
}

export function createBookExportCapabilityMatrix(
  tier: BookExportTier,
): BookExportCapabilityMatrix {
  return {
    allowedModes: collectAllowedValues(bookExportModeRequirements, tier),
    allowedPageSizes: [...bookExportPageSizes],
    allowedPresets: collectAllowedValues(bookExportPresetRequirements, tier),
    allowedTargets: collectAllowedValues(bookExportTargetRequirements, tier),
    allowAppendices: isBookExportTierAllowed(tier, bookExportAppendixRequiredTier),
    allowIllustrations: isBookExportTierAllowed(tier, bookExportIllustrationsRequiredTier),
    allowMirroredMargins: isBookExportTierAllowed(tier, bookExportMirroredMarginsRequiredTier),
    allowPrintMode: isBookExportTierAllowed(tier, bookExportPrintModeRequiredTier),
    allowRightPageStarts: isBookExportTierAllowed(tier, bookExportRightPageStartsRequiredTier),
    appendixRequiredTier: bookExportAppendixRequiredTier,
    defaultConfig: createDefaultBookExportConfigForTier(tier),
    endMatterSourceRequirements: { ...bookExportEndMatterSourceRequirements },
    frontMatterSourceRequirements: { ...bookExportFrontMatterSourceRequirements },
    illustrationsRequiredTier: bookExportIllustrationsRequiredTier,
    mirroredMarginsRequiredTier: bookExportMirroredMarginsRequiredTier,
    modeRequirements: { ...bookExportModeRequirements },
    presetRequirements: { ...bookExportPresetRequirements },
    printModeRequiredTier: bookExportPrintModeRequiredTier,
    recommendedUpgradeTier: getRecommendedBookExportUpgradeTier(tier),
    rightPageStartsRequiredTier: bookExportRightPageStartsRequiredTier,
    targetRequirements: { ...bookExportTargetRequirements },
    tier,
  };
}
